import { useCallback, useEffect, useMemo, useState } from "react";

import { Box, Center, Flex, Image, useDisclosure } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import { useShallow } from "zustand/react/shallow";

import { CommonListType, InboundingList } from "__graphql__/types";
import { HEIGHT } from "app/components/Header";
import { routes } from "config/routes";
import { DeleteCanceledOrderConfirmationModal } from "flows/Inbound/components/DeleteCanceledOrderConfirmationModal";
import { DeleteListConfirmationModal } from "flows/Inbound/components/DeleteListConfirmationModal";
import { DraftListExistsModalOpen } from "flows/Inbound/components/DraftListExistsModalOpen";
import { EndDroppingProcessButton } from "flows/Inbound/components/EndDroppingProcessButton";
import { NoMatchingListModal } from "flows/Inbound/components/NoMatchingListModal";
import { SharedListComponent } from "flows/Inbound/components/SharedListComponent";
import { UnclaimListModal } from "flows/Inbound/components/UnclaimListModal";
import { UseAutoCreatedListConfirmationModal } from "flows/Inbound/components/UseAutoCreatedListConfirmationModal";
import { useGetInboundingListsQuery } from "flows/Inbound/queries/collaborativeInbound/collaborativeInbound.generated";
import { useInboundStore } from "flows/Inbound/stores/inboundStore/useInboundStore";
import { mapInboundingTypeToDeliveryType } from "flows/Inbound/stores/inboundStore/utils";
import { useClaimInboundList } from "flows/Inbound/stores/useInboundDropService";
import {
  useDeleteSharedInboundList,
  useHandleDraftListContinuation,
} from "flows/Inbound/stores/useInboundPreDropService";
import { useInboundPreviewService } from "flows/Inbound/stores/useInboundPreviewService";
import DeliveryTruckNew from "images/random/delivery-truck-new.svg";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { useScan } from "shared/hooks/useScan/useScan";
import { IntlMessageId } from "shared/lang";
import { BOTTOM_TAB_BAR_HEIGHT_NUMBER } from "ui/BottomTabBarContainer";
import { Spinner } from "ui/Spinner/Spinner";
import { BodyM, HeaderS, TitleM } from "ui/Typography/Typography";
import { cleanSSCC, isValidSSCCFormat } from "utils/scan";

function InboundSharedListPlaceholder() {
  return (
    <Flex
      h={`calc(100% - ${HEIGHT})`}
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      px="s100"
      gap="s100"
    >
      <HeaderS textAlign="center">
        <FormattedMessage id="flows.inbound.page.inbound-shared-list.header" />
      </HeaderS>
      <BodyM textAlign="center">
        <FormattedMessage id="flows.inbound.page.inbound-shared-list.paragraph" />
      </BodyM>
      <Image src={DeliveryTruckNew} mt="s200" />
    </Flex>
  );
}

export function InboundSharedList() {
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { isClaimingList } = useClaimInboundList();
  const {
    categorizedLists,
    inboundUIState,
    setCategorizedLists,
    setDeliverySSCC,
    setScannedSSCC,
    setSelectedOption,
    setSharedListId,
    setSharedListName,
    setInboundUIState,
    resetToInitialState,
  } = useInboundStore(
    useShallow((state) => ({
      categorizedLists: state.categorizedLists,
      inboundUIState: state.inboundUIState,
      setCategorizedLists: state.setCategorizedLists,
      setDeliverySSCC: state.setDeliverySSCC,
      setScannedSSCC: state.setScannedSSCC,
      setSelectedOption: state.setSelectedOption,
      setSharedListId: state.setSharedListId,
      setSharedListName: state.setSharedListName,
      setInboundUIState: state.setInboundUIState,
      resetToInitialState: state.resetToInitialState,
    })),
  );

  useEffect(() => {
    resetToInitialState();
  }, [resetToInitialState]);

  const {
    data,
    loading: isFetchingShareList,
    refetch: refetchInboundingLists,
  } = useGetInboundingListsQuery({
    fetchPolicy: "network-only",
  });
  const { deleteSharedInboundList, isDeletingSharedInboundList } =
    useDeleteSharedInboundList(refetchInboundingLists);

  useEffect(() => {
    const sharedLists = data?.getInboundingLists?.lists || [];
    setCategorizedLists(sharedLists);
  }, [data?.getInboundingLists?.lists, setCategorizedLists]);

  const hasDraftList = categorizedLists.draft.length > 0;
  const latestDraftList = categorizedLists.draft[0];
  const hasSharedLists = Object.values(categorizedLists).some((lists) => lists.length > 0);

  const { handleDraftListContinuation } = useHandleDraftListContinuation();

  const [selectedListId, setSelectedListId] = useState<string | null>(null);
  const {
    onOpen: onOpenDeleteListConfirmationModal,
    isOpen: isDeleteListConfirmationModalOpen,
    onClose: onCloseDeleteListConfirmationModal,
  } = useDisclosure();
  const {
    onOpen: onOpenDeleteCanceledOrderConfirmationModal,
    isOpen: isDeleteCanceledOrderConfirmationModalOpen,
    onClose: onCloseDeleteCanceledOrderConfirmationModal,
  } = useDisclosure();

  const {
    onOpen: onOpenNoMatchingListModal,
    isOpen: isNoMatchingListModalOpen,
    onClose: onCloseNoMatchingListModal,
  } = useDisclosure();
  const {
    onOpen: onOpenDraftListExistsModal,
    isOpen: isDraftListExistsModalOpen,
    onClose: onCloseDraftListExistsModal,
  } = useDisclosure();
  const {
    onOpen: onOpenUseAutoCreatedListConfirmationModal,
    isOpen: isUseAutoCreatedListConfirmationModalOpen,
    onClose: onCloseUseAutoCreatedListConfirmationModal,
  } = useDisclosure();

  const listsById = useMemo(() => {
    const map = new Map<string, InboundingList>();
    Object.values(categorizedLists)
      .flat()
      .forEach((list) => {
        map.set(list.id, list);
      });
    return map;
  }, [categorizedLists]);

  const handleOpenDeleteListModal = (listId: string) => {
    setSelectedListId(listId);
    const list = listsById.get(listId);
    if (!list) return;

    if (list.listType === CommonListType.list_type_cancelled_order) {
      onOpenDeleteCanceledOrderConfirmationModal();
    } else {
      onOpenDeleteListConfirmationModal();
    }
  };

  const onDeleteSharedList = useCallback(() => {
    const listId = selectedListId;
    if (!listId) return;

    const selectedList = listsById.get(listId);
    setSelectedListId(null);
    onCloseDeleteListConfirmationModal();
    onCloseDeleteCanceledOrderConfirmationModal();

    if (!selectedList) return;

    deleteSharedInboundList(selectedList);
  }, [
    deleteSharedInboundList,
    listsById,
    onCloseDeleteCanceledOrderConfirmationModal,
    onCloseDeleteListConfirmationModal,
    selectedListId,
  ]);

  const onClickContinue = useCallback(async () => {
    if (!latestDraftList.id) return;
    if (isDraftListExistsModalOpen) {
      onCloseDraftListExistsModal();
    }
    await handleDraftListContinuation(latestDraftList);
  }, [
    handleDraftListContinuation,
    isDraftListExistsModalOpen,
    latestDraftList,
    onCloseDraftListExistsModal,
  ]);

  const navigateToCreateNewList = useCallback(() => {
    navigate(routes.inbound.selectDelivery);
  }, [navigate]);

  const onClickCreateNewList = useCallback(() => {
    navigateToCreateNewList();
  }, [navigateToCreateNewList]);

  const { fetchInboundPreviewProducts } = useInboundPreviewService();
  const [isScanning, setIsScanning] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [localMatchedSSCC, setLocalMatchedSSCC] = useState<InboundingList | null>(null);

  const handleCreateNewListWithSSCC = useCallback(() => {
    onCloseNoMatchingListModal();
    navigateToCreateNewList();
    setIsScanning(false);
  }, [navigateToCreateNewList, onCloseNoMatchingListModal]);

  const handleCancelCreateNewListWithSSCC = useCallback(() => {
    onCloseNoMatchingListModal();
    setScannedSSCC(null);
    setIsScanning(false);
  }, [onCloseNoMatchingListModal, setScannedSSCC]);

  const handleClickContinueDraftList = useCallback(() => {
    onClickContinue();
    setIsScanning(false);
  }, [onClickContinue]);

  const handleOnCloseDraftListExistsModal = useCallback(() => {
    onCloseDraftListExistsModal();
    setIsScanning(false);
  }, [onCloseDraftListExistsModal]);

  const handleMatchedSSCC = useCallback(
    async (matchedSSCC: InboundingList) => {
      setIsLoadingPreview(true);
      try {
        setSharedListId(matchedSSCC.id);
        setSharedListName(matchedSSCC.name);
        setSelectedOption(mapInboundingTypeToDeliveryType(matchedSSCC.inboundingType));
        setInboundUIState({ isOrderCanceled: false });
        setDeliverySSCC(matchedSSCC.sscc);

        await fetchInboundPreviewProducts(matchedSSCC.products);
      } catch (e) {
        showToastUI({
          id: "fetch-products-failed",
          title: "create-shared-list-error-toast",
        });
      } finally {
        setIsScanning(false);
        setIsLoadingPreview(false);
      }
    },
    [
      fetchInboundPreviewProducts,
      setDeliverySSCC,
      setInboundUIState,
      setSelectedOption,
      setSharedListId,
      setSharedListName,
      showToastUI,
    ],
  );

  const handleUseAutoCreatedList = useCallback(() => {
    if (isUseAutoCreatedListConfirmationModalOpen) {
      onCloseUseAutoCreatedListConfirmationModal();
    }
    if (!localMatchedSSCC) return;
    handleMatchedSSCC(localMatchedSSCC);
    setScannedSSCC(localMatchedSSCC.sscc);
    setLocalMatchedSSCC(null);
    setIsScanning(false);
  }, [
    localMatchedSSCC,
    setScannedSSCC,
    setLocalMatchedSSCC,
    handleMatchedSSCC,
    onCloseUseAutoCreatedListConfirmationModal,
    isUseAutoCreatedListConfirmationModalOpen,
  ]);

  const handleCreateListManuallyAndDeleteAutoCreatedList = useCallback(() => {
    if (isUseAutoCreatedListConfirmationModalOpen) {
      onCloseUseAutoCreatedListConfirmationModal();
    }
    if (!localMatchedSSCC) return;
    setScannedSSCC(localMatchedSSCC.sscc);
    setLocalMatchedSSCC(null);
    setIsScanning(false);

    navigateToCreateNewList();
    onDeleteSharedList();
  }, [
    localMatchedSSCC,
    setIsScanning,
    setScannedSSCC,
    onDeleteSharedList,
    navigateToCreateNewList,
    onCloseUseAutoCreatedListConfirmationModal,
    isUseAutoCreatedListConfirmationModalOpen,
  ]);

  const findMatchedSSCC = useCallback(
    (scannedValue: string) => {
      const normalizedSSCC = cleanSSCC(scannedValue);

      return categorizedLists.unclaimed.find(
        (list) =>
          list.creatorId === "system" &&
          list.listType === CommonListType.list_type_regular &&
          (list?.sscc === normalizedSSCC || list?.sscc === scannedValue),
      );
    },
    [categorizedLists.unclaimed],
  );

  useScan({
    onScan: (scannedValue) => {
      if (isScanning) return;
      setIsScanning(true);
      setScannedSSCC(null);

      if (!isValidSSCCFormat(scannedValue)) {
        showToastUI({
          id: "invalid_rolli_format_toast",
          title: "flows.inbound.scan-rolli.invalid-rolli-format.title",
        });
        setIsScanning(false);
        return;
      }

      const matchedSSCC = findMatchedSSCC(scannedValue);
      if (matchedSSCC) {
        setLocalMatchedSSCC(matchedSSCC);
        setSelectedListId(matchedSSCC.id);
        onOpenUseAutoCreatedListConfirmationModal();
        return;
      }

      if (hasDraftList) {
        onOpenDraftListExistsModal();
        return;
      }

      // No match found and no draft: prompt to create new list
      setScannedSSCC(scannedValue);
      onOpenNoMatchingListModal();
    },
  });

  if (isFetchingShareList || isClaimingList || isDeletingSharedInboundList || isLoadingPreview) {
    return (
      <Center h="100%" w="100%">
        <Spinner />
      </Center>
    );
  }

  return (
    <Flex flex={1} flexDirection="column" alignItems="center" justifyContent="flex-start" px="s100">
      {hasSharedLists ? (
        <Flex w="100%" flexDirection="column" justifyContent="flex-start" gap="s100" pb="72px">
          {Object.entries(categorizedLists).map(
            ([category, lists]) =>
              lists.length > 0 && (
                <Flex key={category} flexDirection="column" justifyContent="flex-start" gap="s100">
                  <Box px="s200" pt="s200">
                    <TitleM textAlign="left">
                      <FormattedMessage
                        id={
                          `flows.inbound.page.inbound-shared-list.inbound-list.${category}` as IntlMessageId
                        }
                      />
                    </TitleM>
                  </Box>
                  {lists.map((sharedList) => (
                    <SharedListComponent
                      key={sharedList.id}
                      creatorId={sharedList.creatorId}
                      orderId={sharedList.orderId ? sharedList.orderId : null}
                      listId={sharedList.id}
                      claimerIds={sharedList.claimerIds}
                      isOrderCanceled={
                        sharedList.listType === CommonListType.list_type_cancelled_order
                      }
                      listName={sharedList.name}
                      deliverySSCC={sharedList.sscc}
                      inboundingType={sharedList.inboundingType}
                      createdAt={sharedList.createdAt}
                      status={sharedList.status}
                      products={sharedList.products}
                      onOpenDeleteListConfirmationModal={() => {
                        if (isDeletingSharedInboundList) return;
                        handleOpenDeleteListModal(sharedList.id);
                      }}
                      refetchInboundingLists={refetchInboundingLists}
                    />
                  ))}
                </Flex>
              ),
          )}
        </Flex>
      ) : (
        <InboundSharedListPlaceholder />
      )}
      <EndDroppingProcessButton
        shouldShowButton
        onClickButton={hasDraftList ? onClickContinue : onClickCreateNewList}
        labelMessageId={
          hasDraftList
            ? "flows.inbound.page.inbound-shared-list.button.continue"
            : "flows.inbound.page.inbound-shared-list.button"
        }
        isLoading={false}
        bottom={BOTTOM_TAB_BAR_HEIGHT_NUMBER}
        px="s200"
      />
      {isDeleteListConfirmationModalOpen && (
        <DeleteListConfirmationModal
          onClickYes={onDeleteSharedList}
          onClickNo={onCloseDeleteListConfirmationModal}
        />
      )}
      {isDeleteCanceledOrderConfirmationModalOpen && (
        <DeleteCanceledOrderConfirmationModal
          onClickYes={onDeleteSharedList}
          onClickNo={onCloseDeleteCanceledOrderConfirmationModal}
        />
      )}
      {inboundUIState.isUnclaimListModalVisible && (
        <UnclaimListModal refetchInboundingLists={refetchInboundingLists} />
      )}
      {isNoMatchingListModalOpen && (
        <NoMatchingListModal
          onClickYes={handleCreateNewListWithSSCC}
          onClickNo={handleCancelCreateNewListWithSSCC}
        />
      )}
      {isDraftListExistsModalOpen && (
        <DraftListExistsModalOpen
          onClickYes={handleClickContinueDraftList}
          onClickNo={handleOnCloseDraftListExistsModal}
        />
      )}
      {isUseAutoCreatedListConfirmationModalOpen && (
        <UseAutoCreatedListConfirmationModal
          onClickYes={handleUseAutoCreatedList}
          onClickNo={handleCreateListManuallyAndDeleteAutoCreatedList}
        />
      )}
    </Flex>
  );
}
